/* =========================================================
   Certificates from a template, made in the browser:
   the Canva design (without name and date) + the student's name + the date
   -> a JPG and a PDF (A4), ready for the usual certificate upload.
   ========================================================= */

// Fonts you can pick for the name and the date (free Google fonts; Canva Sans can't be downloaded)
export const FONTS = [
  'Charm',
  'Great Vibes',
  'Dancing Script',
  'Alex Brush',
  'Open Sans',
  'Poppins',
  'Montserrat',
  'Inter',
  'Lato',
  'Nunito Sans',
];

export const DATE_FORMATS = [
  ['dayShort', '28 Oct 2026'],
  ['long', 'October 28, 2026'],
  ['dayLong', '28 October 2026'],
  ['dmy', '28/10/2026'],
  ['mdy', '10/28/2026'],
];

export const DEFAULT_CONFIG = {
  name: { x: 0.5, y: 0.5, size: 0.075, font: 'Charm', weight: 700, color: '#1B2A4A', align: 'center', maxWidth: 0.7 },
  date: { x: 0.5, y: 0.68, size: 0.024, font: 'Open Sans', weight: 400, color: '#1B2A4A', align: 'center', format: 'dayShort' },
};

// a saved config + the defaults for anything missing
export const withDefaults = (config) => ({
  name: { ...DEFAULT_CONFIG.name, ...(config?.name || {}) },
  date: { ...DEFAULT_CONFIG.date, ...(config?.date || {}) },
});

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const formatDate = (iso, fmt = 'long') => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso || '');
  const [y, mo, d] = [m[1], Number(m[2]), Number(m[3])];
  if (fmt === 'dayShort') return `${d} ${MONTHS[mo - 1].slice(0, 3)} ${y}`;
  if (fmt === 'dayLong') return `${d} ${MONTHS[mo - 1]} ${y}`;
  if (fmt === 'dmy') return `${String(d).padStart(2, '0')}/${String(mo).padStart(2, '0')}/${y}`;
  if (fmt === 'mdy') return `${String(mo).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
  return `${MONTHS[mo - 1]} ${d}, ${y}`;
};

/* ---------- fonts ---------- */
const loadedFamilies = new Set();
function addFontCss(family) {
  if (loadedFamilies.has(family)) return;
  loadedFamilies.add(family);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;600;700&display=swap`;
  document.head.appendChild(link);
}

export async function ensureFonts(config) {
  const c = withDefaults(config);
  const want = [c.name, c.date];
  want.forEach((t) => addFontCss(t.font));
  if (!document.fonts?.load) return;
  // wait for the stylesheet, then for the font itself (a canvas doesn't wait by itself)
  for (let i = 0; i < 20; i += 1) {
    try {
      const ok = await Promise.all(want.map((t) => document.fonts.load(`${t.weight} 40px "${t.font}"`)));
      if (ok.every((list) => list.length)) return;
    } catch {
      /* try again */
    }
    await new Promise((r) => setTimeout(r, 150));
  }
}

/* ---------- template picture ---------- */
// The picture comes through the API (with the login), as a blob: a picture straight from /assets
// can't be drawn into a file we then save, when the server doesn't send cross-origin permission.
export async function loadTemplateImage(moduleId, plan = 'all') {
  const API = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem('userData');
  const res = await fetch(`${API}/api/admin/certificate-templates/${moduleId}/${plan}/picture`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: 'no-store',
  });
  if (!res.ok) throw new Error('Couldn’t open the template picture.');
  const url = URL.createObjectURL(await res.blob());
  try {
    return await loadImage(url);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}

// a fresh copy (the page's cached copy can't be drawn on a canvas that we then export)
const fresh = (url) => (/^data:|^blob:/.test(url) ? url : `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`);
export function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!/^data:|^blob:/.test(url)) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Couldn’t open the template picture.'));
    img.src = fresh(url);
  });
}

/* ---------- drawing ---------- */
function drawText(ctx, text, t, W, H) {
  if (!text) return;
  let px = Math.round(t.size * H);
  ctx.font = `${t.weight} ${px}px "${t.font}"`;
  // long names get smaller so they stay inside the space
  const maxW = (t.maxWidth || 0.9) * W;
  while (px > 8 && ctx.measureText(text).width > maxW) {
    px -= 2;
    ctx.font = `${t.weight} ${px}px "${t.font}"`;
  }
  ctx.fillStyle = t.color;
  ctx.textAlign = t.align || 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, t.x * W, t.y * H);
}

/* template (Image) + config + { name, date (YYYY-MM-DD) } -> canvas at the template's full size */
// draw on any canvas size (the editor preview uses a small one, the file the full size)
export function paintCertificate(canvas, img, config, { name, date }) {
  const c = withDefaults(config);
  const W = canvas.width;
  const H = canvas.height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, W, H);
  ctx.drawImage(img, 0, 0, W, H);
  drawText(ctx, String(name || '').trim(), c.name, W, H);
  drawText(ctx, formatDate(date, c.date.format), c.date, W, H);
}

export function renderCertificate(img, config, data) {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  paintCertificate(canvas, img, config, data);
  return canvas;
}

export const canvasToJpeg = (canvas, quality = 0.92) =>
  new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Couldn’t make the image.'))), 'image/jpeg', quality)
  );

/* A one-page PDF (A4, landscape or portrait like the picture) holding the JPG */
export async function jpegToPdf(jpegBlob, widthPx, heightPx) {
  const jpeg = new Uint8Array(await jpegBlob.arrayBuffer());
  const landscape = widthPx >= heightPx;
  const PW = landscape ? 841.89 : 595.28;
  const PH = landscape ? 595.28 : 841.89;
  const enc = new TextEncoder();
  const parts = [];
  const offsets = [];
  let length = 0;
  const push = (x) => {
    const bytes = typeof x === 'string' ? enc.encode(x) : x;
    parts.push(bytes);
    length += bytes.length;
  };
  const obj = (n, body) => {
    offsets[n] = length;
    push(`${n} 0 obj\n${body}\nendobj\n`);
  };

  push('%PDF-1.4\n');
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a])); // binary marker
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
  obj(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  obj(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PW} ${PH}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`);
  offsets[4] = length;
  push(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${widthPx} /Height ${heightPx} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
  push(jpeg);
  push('\nendstream\nendobj\n');
  const content = `q ${PW} 0 0 ${PH} 0 0 cm /Im0 Do Q`;
  obj(5, `<< /Length ${content.length} >>\nstream\n${content}\nendstream`);

  const xref = length;
  let table = 'xref\n0 6\n0000000000 65535 f \n';
  for (let i = 1; i <= 5; i += 1) table += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  push(table);
  push(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return new Blob(parts, { type: 'application/pdf' });
}

/* Everything at once: module id (or a picture url) + config + name/date -> { jpg: File, pdf: File, previewUrl } */
export async function makeCertificate({ moduleId, plan = 'all', templateUrl, config, name, date, fileBase = 'certificate' }) {
  await ensureFonts(config);
  const img = moduleId ? await loadTemplateImage(moduleId, plan) : await loadImage(templateUrl);
  const canvas = renderCertificate(img, config, { name, date });
  const jpgBlob = await canvasToJpeg(canvas);
  const pdfBlob = await jpegToPdf(jpgBlob, canvas.width, canvas.height);
  const safe = String(fileBase).replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'certificate';
  return {
    jpg: new File([jpgBlob], `${safe}.jpg`, { type: 'image/jpeg' }),
    pdf: new File([pdfBlob], `${safe}.pdf`, { type: 'application/pdf' }),
    previewUrl: URL.createObjectURL(jpgBlob),
  };
}

/* The student's level (IdGradesPlans: 1 = Little, 2 = Junior) -> which design of a module to use:
   the level's own design first, else the one for everyone. Returns { plan, url, config } or null. */
export const planOf = (idGradesPlans) => (Number(idGradesPlans) === 1 ? 'little' : Number(idGradesPlans) === 2 ? 'junior' : null);
export function pickDesign(designs, idGradesPlans) {
  if (!designs) return null;
  const own = planOf(idGradesPlans);
  if (own && designs[own]) return { plan: own, ...designs[own] };
  if (designs.all) return { plan: 'all', ...designs.all };
  return null;
}
